import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { FormEvent, useState } from 'react'

import { type LiveTerminal } from '@iws/backoffice-api'

import { Badge, Card, CardContent, CardHeader, CardTitle, Input, PosButton } from '@/components/ui'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { PageHeader } from '@/components/pos/PageHeader'
import { useOfficeQuery } from '@/hooks/features/office'
import { useSession, useSyncState } from '@/contexts/SessionContext'
import { rows } from '@/db/database'
import { TerminalModel } from '@/http/models'
import { syncEngine } from '@/sync/SyncEngine'
import { formatMoney, formatTime } from '@/utils'

const minutesAgo = (iso: string | null) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000)) : null)

export const SyncCell = ({ t }: { t: LiveTerminal }) => {
  const ago = minutesAgo(t.last_synced_at)
  const dot = t.sync_status === 'online' ? 'bg-success' : t.sync_status === 'offline' ? 'bg-warning' : 'bg-grey-500'
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-text-2">
      <span className={`size-2 rounded-full ${dot}`} />
      {t.sync_status === 'online' ? 'online' : t.sync_status === 'idle' ? 'idle' : ago === null ? 'offline · never synced' : `offline · ${ago} min`}
    </span>
  )
}

/**
 * The branch's terminals. Creating one needs the server (it numbers terminals, Q27), so this works online only;
 * everything else in the till works offline. Staff belong to branches, not terminals: any of the branch's staff can
 * set a device up as any of its terminals.
 */
export const TerminalsPage = () => {
  const { device, can } = useSession()
  const sync = useSyncState()
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)
  const online = sync.status !== 'offline' && sync.status !== 'auth'

  // Online: each terminal's open shift from the server (POS.md §18, Terminals page); offline: the list on the device.
  const live = useOfficeQuery(['terminals-live'], () => TerminalModel.live(device!.branch_id))
  const requestClose = async (t: LiveTerminal) => {
    if (!t.shift) return
    const res = await TerminalModel.requestClose(t.shift._id)
    if (!res.success) return toast.error(res.error?.message ?? 'Could not ask to close the shift.')
    toast.success(`T${t.unique_no} will ask to close its shift on its next sync`)
    live.refetch()
  }

  const { data: terminals = [] } = useQuery({
    queryKey: ['terminals', sync.lastSyncedAt],
    queryFn: async () =>
      (await rows('terminals').toArray())
        .filter((t) => !t.deleted_at && t.branch_id === device?.branch_id)
        .sort((a, b) => Number(a.unique_no) - Number(b.unique_no)),
  })

  const onCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!device) return
    const form = e.currentTarget
    setBusy(true)
    const res = await TerminalModel.create({
      name: String(new FormData(form).get('name')).trim(),
      branch_id: device.branch_id,
    })
    setBusy(false)
    if (!res.success) return toast.error(res.error?.message ?? 'Could not create the terminal.')
    toast.success(`Terminal T${res.data?.result.unique_no} created`)
    form.reset()
    syncEngine.requestSync()
    client.invalidateQueries({ queryKey: ['terminals'] })
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Terminals" subtitle="This branch's terminals. Adding one needs a connection." />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle>Terminals</CardTitle>
            </CardHeader>
            <CardContent>
              {live.data ? (
                <OfficeTable<LiveTerminal>
                  cards
                  rows={live.data.result}
                  rowKey={(t) => t._id}
                  columns={[
                    {
                      label: 'Terminal',
                      cell: (t) => (
                        <span className="font-medium text-ink">
                          T{t.unique_no} · {t.name}
                          {t._id === device?.terminal_id && <span className="ml-1.5 text-xs font-normal text-muted-foreground">this device</span>}
                        </span>
                      ),
                    },
                    { label: 'Cashier', cell: (t) => t.shift?.staff?.name ?? '—' },
                    {
                      label: 'Shift',
                      cell: (t) =>
                        !t.shift ? (
                          <Badge tone="soft">Closed</Badge>
                        ) : (
                          <span className="inline-flex flex-wrap items-center justify-end gap-1">
                            <Badge tone={t.shift.long_open ? 'warning' : 'success'}>
                              {t.shift.long_open ? 'Open > 12 h' : `Open ${formatTime(t.shift.opened_at)}`}
                            </Badge>
                            {t.shift.close_requested_at && <Badge tone="warning">Close asked</Badge>}
                            {t.shift.shift_no && <span className="font-mono text-xs text-muted-foreground">{t.shift.shift_no}</span>}
                          </span>
                        ),
                    },
                    { label: 'Drawer', className: 'text-right tabular-nums', cell: (t) => (t.shift ? formatMoney(t.shift.drawer) : '—') },
                    {
                      label: 'Sales today',
                      className: 'text-right tabular-nums',
                      cell: (t) => (t.sales_today.orders ? `${formatMoney(t.sales_today.total)} · ${t.sales_today.orders}` : '—'),
                    },
                    { label: 'Sync', cell: (t) => <SyncCell t={t} /> },
                    {
                      label: '',
                      className: 'text-right',
                      cell: (t) =>
                        t.shift && !t.shift.close_requested_at && can('pos:close_shift') && t._id !== device?.terminal_id ? (
                          <PosButton variant="outline" onClick={() => requestClose(t)}>
                            Ask to close
                          </PosButton>
                        ) : null,
                    },
                  ]}
                />
              ) : (
              <ul className="divide-y divide-border-subtle">
                {terminals.map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="text-ink">
                      T{String(t.unique_no)} · {String(t.name)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t._id === device?.terminal_id ? 'This device' : t.is_open ? 'Shift open' : ''}
                    </span>
                  </li>
                ))}
              </ul>
              )}
            </CardContent>
          </Card>
          {can('terminals:create') && (
            <Card>
              <CardHeader>
                <CardTitle>New terminal</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={onCreate} className="space-y-3">
                  <Input name="name" placeholder="Name, e.g. Counter 2" required className="h-11" />
                  <PosButton type="submit" size="touch" className="w-full" disabled={busy || !online}>
                    Create terminal
                  </PosButton>
                  {!online && <p className="text-xs text-muted-foreground">Needs a connection and a signed-in session.</p>}
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
